import { CustomLayerInterface, CustomRenderMethodInput, Map as MaplibreMap } from 'maplibre-gl';
import { getTwilightData, Twilights } from '../util/sun-calculator';
import fragmentSource from './day-night.fragment.glsl?raw';
import vertexSource from './day-night.vertex.glsl?raw';
import { generateRectangularMesh } from '../util/mesh-utils';

// create a custom style layer to implement the WebGL content
export class DayNightLayer implements CustomLayerInterface {
    readonly type = 'custom';
    readonly id: string;
    private aPos: GLint = 0;
    private vertexBuffer: WebGLBuffer | null = null;
    private indexBuffer: WebGLBuffer | null = null;
    private globeVertexCount: number = 0;
    private map: MaplibreMap | null = null;
    private visibleTwilights: Twilights[] = [];
    private date: Date = new Date();
    private radius: number[] = [];
    private center: [number, number] = [0, 0];
    private opacity: number = 0.22;
    private basic: boolean = false;
    private debug = false;
    private shaderMap = new Map<string, WebGLProgram>();

    constructor(id: string) {
        this.id = id;
    }

    // Helper method for creating a shader based on current map projection - globe will automatically switch to mercator when some condition is fulfilled.
    getShader(gl: WebGLRenderingContext | WebGL2RenderingContext, shaderDescription: CustomRenderMethodInput['shaderData']) {
        // Pick a shader based on the current projection, defined by `variantName`.
        if (this.shaderMap.has(shaderDescription.variantName)) {
            return this.shaderMap.get(shaderDescription.variantName);
        }

        const vs = vertexSource
            .replace('__define__', shaderDescription.define)
            .replace('__prelude__', shaderDescription.vertexShaderPrelude);

        // create a vertex shader
        const vertexShader = gl.createShader(gl.VERTEX_SHADER)!;
        gl.shaderSource(vertexShader, vs);
        gl.compileShader(vertexShader);
        if (!gl.getShaderParameter(vertexShader, gl.COMPILE_STATUS)) {
            console.error(gl.getShaderInfoLog(vertexShader));
        }

        // create a fragment shader
        const fragmentShader = gl.createShader(gl.FRAGMENT_SHADER)!;
        gl.shaderSource(fragmentShader, fragmentSource);
        gl.compileShader(fragmentShader);
        if (!gl.getShaderParameter(fragmentShader, gl.COMPILE_STATUS)) {
            console.error(gl.getShaderInfoLog(fragmentShader));
        }

        // link the two shaders into a WebGL program
        const program = gl.createProgram();
        gl.attachShader(program, vertexShader);
        gl.attachShader(program, fragmentShader);
        gl.linkProgram(program);

        this.aPos = gl.getAttribLocation(program, 'a_pos');

        this.shaderMap.set(shaderDescription.variantName, program);

        return program;
    }

    onAdd(map: MaplibreMap, gl: WebGLRenderingContext | WebGL2RenderingContext) {
        this.map = map;

        const mesh = generateRectangularMesh([-180, -85, 180, 85], 1);

        this.vertexBuffer = gl.createBuffer();
        gl.bindBuffer(gl.ARRAY_BUFFER, this.vertexBuffer);
        gl.bufferData(gl.ARRAY_BUFFER, new Float32Array(mesh.verticesFlattened), gl.STATIC_DRAW);

        this.indexBuffer = gl.createBuffer();
        gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, this.indexBuffer);
        gl.bufferData(gl.ELEMENT_ARRAY_BUFFER, new Uint16Array(mesh.triangleIndices), gl.STATIC_DRAW);
        this.globeVertexCount = mesh.triangleIndices.length;
    }

    updateTwilights(twilights: Twilights[], opacity: number) {
        this.visibleTwilights = twilights;
        this.opacity = opacity;
        this.basic = false;
        this.refresh();
    }

    setBasic(opacity: number) {
        this.opacity = opacity;
        this.visibleTwilights = ['sunrise', 'civil'];
        this.basic = true;
        this.refresh();
    }

    setTime(time: Date) {
        this.date = time;
        this.refresh();
    }

    off() {
        this.opacity = 0.0;
        this.visibleTwilights = [];
        this.refresh();
    }

    refresh() {
        const data = getTwilightData(this.date, this.visibleTwilights);
        this.radius = [...data.map((x) => x.radius), 0, 0, 0, 0].slice(0, 4);
        this.center = data[0]?.center ?? [0, 0];
        this.map?.triggerRepaint();
    }

    // method fired on each animation frame
    render(gl: WebGLRenderingContext | WebGL2RenderingContext, args: CustomRenderMethodInput) {
        if (!this.vertexBuffer) {
            return;
        }

        if (this.visibleTwilights.length === 0) {
            return;
        }

        const globe = args.shaderData.variantName === 'globe';
        (globe ? [0] : this.getWorldOffsets()).forEach((offset) => {
            const program = this.getShader(gl, args.shaderData)!;
            gl.useProgram(program);
            gl.uniformMatrix4fv(
                gl.getUniformLocation(program, 'u_projection_fallback_matrix'),
                false,
                args.defaultProjectionData.fallbackMatrix // convert mat4 from gl-matrix to a plain array
            );
            gl.uniformMatrix4fv(
                gl.getUniformLocation(program, 'u_projection_matrix'),
                false,
                args.defaultProjectionData.mainMatrix // convert mat4 from gl-matrix to a plain array
            );
            gl.uniform4f(
                gl.getUniformLocation(program, 'u_projection_tile_mercator_coords'),
                ...args.defaultProjectionData.tileMercatorCoords
            );

            gl.uniform4f(gl.getUniformLocation(program, 'u_projection_clipping_plane'), ...args.defaultProjectionData.clippingPlane);
            gl.uniform1f(gl.getUniformLocation(program, 'u_projection_transition'), args.defaultProjectionData.projectionTransition);
            gl.uniform1f(gl.getUniformLocation(program, 'u_worldXOffset'), offset);
            gl.uniform4f(gl.getUniformLocation(program, 'u_radius'), ...(this.radius as [number, number, number, number]));
            gl.uniform2f(gl.getUniformLocation(program, 'u_center'), ...this.center);
            gl.uniform1f(gl.getUniformLocation(program, 'u_opacity'), this.opacity);
            gl.uniform1f(gl.getUniformLocation(program, 'u_basic'), this.basic ? 1.0 : 0.0);

            gl.bindBuffer(gl.ARRAY_BUFFER, this.vertexBuffer);
            gl.enableVertexAttribArray(this.aPos);
            gl.vertexAttribPointer(this.aPos, 2, gl.FLOAT, false, 0, 0);

            gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, this.indexBuffer);

            gl.uniform1f(gl.getUniformLocation(program, 'u_debug'), 0.0);
            gl.drawElements(gl.TRIANGLES, this.globeVertexCount, gl.UNSIGNED_SHORT, 0);

            if (this.debug) {
                gl.uniform1f(gl.getUniformLocation(program, 'u_debug'), 1.0);
                gl.drawElements(gl.LINE_STRIP, this.globeVertexCount, gl.UNSIGNED_SHORT, 0);
            }
        });
    }

    onRemove(map: MaplibreMap, gl: WebGLRenderingContext | WebGL2RenderingContext): void {
        this.map = null;
        gl.deleteBuffer(this.vertexBuffer);
        gl.deleteBuffer(this.indexBuffer);

        this.indexBuffer = null;
        this.indexBuffer = null;

        for (const program in this.shaderMap.values()) {
            gl.deleteProgram(program);
        }

        this.shaderMap.clear();
    }

    private getWorldOffsets() {
        const bounds = this.map!.getBounds();
        const west = bounds.getWest();
        const east = bounds.getEast();

        const x1 = 0 - Math.ceil((Math.abs(west) - 180) / 360);
        const x2 = Math.ceil((Math.abs(east) - 180) / 360);

        const worldOffsets = [];
        for (let i = x1; i <= x2; i++) {
            worldOffsets.push(i);
        }
        return worldOffsets;
    }
}
